/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the reset password content Page
 * The URI keyword would have to match something like 'mot-de-passe-oublie'
 * It will show a form allowing the user to ask for a token through email
 */

// Imports
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { toast } from 'react-toastify';

// Interfaces
import { PublicCabinet } from '../../../interfaces/ICabinets';

// Utils
import { ApiCallConstructor, GlobalApiCall, HTTPResponse } from "../../utils/ApiCallConstructor";
import { useIsMobileScreen } from '../../utils/MobileScreenContext';

// Buroclic adaptive componentts
import { PoweredByBc } from '../../buroclic/PoweredByBc';

export const ForgottenPwdPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const isMobileScreen = useIsMobileScreen();

  const tmpCabinet :PublicCabinet = JSON.parse(localStorage.getItem('cabinet') || "{}") as PublicCabinet
  const [cabinet, setCabinet] = useState<PublicCabinet>(tmpCabinet);

  const cabinetAlias = (params.cabinetAlias ? params.cabinetAlias : '');

  // Store cabinetAlias in localStorage
  if (cabinetAlias !== '')
    localStorage.setItem('cabinetAlias', cabinetAlias);

  document.title = cabinetAlias ? cabinetAlias + ' | Espace client' : 'Votre espace client';

  const [login, setLogin] = useState('');
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');


  // Handle email validation

  // check for email validation through RegExp
  const emailValidation = useCallback(() => {
    if (email === '')
      return false;
    else if (!/^[a-zA-Z0-9-_]+(?:\.[a-zA-Z0-9-_]+)*@[a-zA-Z0-9-_]+(?:\.[a-zA-Z0-9]+)*$/i.test(email)) {
      setErrorMessage('L\'email saisi n\'est pas une email valide');
      return false;
    }
    setErrorMessage('');
    return true;
  }, [email]);


  // Initial data
  useEffect(() => {
    if (!tmpCabinet?.host) {
      // Fetch cabinet Back-end data such as Host
      const fetchCabinet = async () => {
        try {
          const cabinetResponse = await toast.promise(
            GlobalApiCall({
              endpoint: `/cabinets/${cabinetAlias}`,
              method: 'GET',
            }),
            {
              pending: 'Liaison avec le cabinet en cours ...',
              error: { render({ data }) { return `Impossible d'établir une laision avec le cabinet : ${(data as HTTPResponse).error}` } },
            });

          // Handle error
          if (cabinetResponse.error !== null)
            throw new Error(cabinetResponse.error);

          const tmpCabinet = (cabinetResponse?.result as PublicCabinet);
          if (!(tmpCabinet.loginBackground)) tmpCabinet.loginBackground = '/images/background.png';
          setCabinet(tmpCabinet);
          localStorage.setItem('cabinet', JSON.stringify(tmpCabinet));
          document.title = cabinetAlias ? cabinetAlias + ' | Espace client' : 'Votre espace client';
        } catch (err: any) {
          localStorage.clear();
          console.error(err.error)
        }
      }

      // load cabinet back-end API
      fetchCabinet();
    }
  }, [cabinetAlias, tmpCabinet])

  useEffect(() => {
    setErrorMessage('');
  }, [login, email]);


  // Submit form
  const ResetPassword = async (e) => {
    e.preventDefault();

    if (login === '') {
      setErrorMessage('Aucun login n\'est renseigné');
      return;
    } else if (email === '') {
      setErrorMessage('Aucun email n\'est renseigné');
      return;
    } else if (!emailValidation())
      return;
    else

      // Reset password through Back-Office (API)
      try {
        await toast.promise(
          ApiCallConstructor({
            endpoint: '/account/password-reset',
            method: 'POST',
            body: {
              login: login,
              email: email,
              dbAlias: cabinet?.dbName,
              url: `${window.location.origin}/${cabinetAlias}/nouveau-mot-de-passe`
            }
          }),
          {
            pending: 'Réinitialisation de votre mot de passe en cours',
            error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
            success: 'Un email vous a été envoyé pour poursuivre la procédure'
          });
        // Redirect to login page
        navigate(`/${cabinetAlias}/login`);
      } catch (err: any) { console.error(err.error) }
  };



  // HTML Templates
  const renderMobile = () => {
    return (
      <div className={`justify-center flex flex-col rounded-sm m-4`} style={{ width: 'calc(100vw - 3rem)' }} >
        <img src={cabinet?.logo} alt="Company logo" className={`h-full w-full object-contain object-center max-h-96 my-4 mx-auto max-w-[75%]`} />

        {/* Forgotten password form */}
        <form onSubmit={ResetPassword} className="bg-[#f7f7f7] border border-black rounded-md mt-8 p-12 pb-0 px-3 pt-2 sm:h-4/5 lg:h-2/3 grow" style={{ width: '-webkit-fill-available' }}>
          <div className='flex flex-col h-full' style={{ height: 'calc(100% - 2rem)' }}>
            <div className='flex flex-col items-start grow'>
              <img src="/images/connexionIcon.png" alt="User icon" className="mx-auto mb-4 w-1/5" />

              {/* Login */}
              <label htmlFor="login" className="text-gray-500 text-4xl mb-3 font-bold inline-block">Login :</label>
              <input type="text" id="login" placeholder='Login' value={login} onChange={(e) => setLogin(e.target.value)}
                className="border border-gray-300 bg-white py-2 px-4 text-4xl rounded-sm h-24 mb-8 shadow-bottom" style={{ width: '-webkit-fill-available' }} />


              {/* Email */}
              <label htmlFor="email" className="text-gray-500 text-4xl mb-3 font-bold inline-block">Email :</label>
              <input type="email" id="email" placeholder='Email' value={email} onChange={(e) => setEmail(e.target.value)}
                className="border border-gray-300 bg-white py-2 px-4 text-4xl rounded-sm h-24 mb-8 shadow-bottom" style={{ width: '-webkit-fill-available' }}
                title="Veuillez entrer une adresse email valide." />

              {/* Error */}
              {errorMessage && <p className="text-red-500 mx-auto font-bold text-4xl">{errorMessage}</p>}

              <button className={`text-white mt-4 text-5xl font-bold py-8 px-12 w-4/5 mb-16 rounded-lg shadow-md mx-auto`} style={{ backgroundColor: cabinet.color }} type="submit">
                Réinitialiser mon mot de passe
              </button>
            </div>

            {/*  Powered by BuroClic */}
            <PoweredByBc className={'text-center py-1'} imgClassName={'h-full w-auto m-auto mb-16'} />
          </div>
        </form>

      </div>
    );
  }

  const renderComputer = () => {
    return (
      <div className="h-full flex items-center justify-center bg-contain bg-no-repeat bg-right-bottom" style={{ backgroundImage: `url('${cabinet.loginBackground ? cabinet.loginBackground : ''}')` }}>
        <div className="w-4/5 justify-center flex flex-col bg-[#f7f7f7] rounded-lg lg:w-1/3 min-h-[40%] h-[40%] lg:mr-auto lg:ml-12 lg:h-[min-content] lg:my-auto">
          <img src={cabinet?.logo} alt="Company logo" className={`h-auto w-full object-contain object-center max-h-[16rem] mt-4 mx-auto max-w-[16rem]`} />

          {/* Forgotten password form */}
          <form onSubmit={ResetPassword} className="border border-black rounded-md p-12 sm:m-3 lg:m-12 pb-0 px-3 pt-2 sm:h-4/5 lg:h-2/3 grow" style={{ width: '-webkit-fill-available' }}>
            <div className='flex flex-col items-start grow' style={{ height: 'calc(100% - 2rem)' }}>
              <img src="/images/connexionIcon.png" alt="User icon" className="mx-auto mb-2 lg:w-1/5 w-10" />

              {/* Login */}
              <label htmlFor="login" className="text-gray-500 font-bold hidden lg:inline-block">Login :</label>
              <input type="text" id="login" placeholder='Login' value={login} onChange={(e) => setLogin(e.target.value)}
                className="border border-gray-300 bg-white py-2 px-2 rounded-md h-10 mb-3 shadow-bottom" style={{ width: '-webkit-fill-available' }} />


              {/* Email */}
              <label htmlFor="email" className="text-gray-500 font-bold hidden lg:inline-block">Email :</label>
              <input type="email" id="email" placeholder='Email' value={email} onChange={(e) => setEmail(e.target.value)}
                className={`border border-gray-300 bg-white py-2 px-2 rounded-md h-10 mb-3 shadow-bottom`}
                title="Veuillez entrer une adresse email valide." style={{ width: '-webkit-fill-available' }} />

              {/* Error */}
              {errorMessage && <p className="text-red-500 mx-auto font-bold ">{errorMessage}</p>}

              <button className={`text-white text-1xl mt-4 font-bold py-2 px-4 px-auto w-52 mb-4 rounded-lg shadow-lg mx-auto`} style={{ backgroundColor: cabinet.color }} type="submit">
                Réinitialiser mon mot de passe
              </button>
            </div>

            {/*  Powered by BuroClic */}
            <PoweredByBc className={'h-8 py-1 mx-auto'} imgClassName={'h-full w-auto m-auto mb-8'} />
          </form>
        </div>
      </div>
    );
  }


  // JSX Template
  return (
    <div className='h-full w-full'>
      {isMobileScreen ? renderMobile() : renderComputer()}
    </div>
  );
};
